import { useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

/** Which part of the booking detail to land on. `overview` unless an action was picked first. */
export const BOOKING_PANEL_STEPS = [
  "overview",
  "move",
  "reschedule",
  "cancel",
  "decline",
  "remove",
  "no_show",
] as const;
export type BookingPanelStep = (typeof BOOKING_PANEL_STEPS)[number];

export interface OpenBookingOptions {
  /** The visit that was clicked. Needed for a series: actions act on that one visit. */
  occurrenceStart?: string | null;
  /** Open straight on an action, e.g. from the calendar's right-click menu. */
  step?: BookingPanelStep;
}

const PANEL_KEYS = ["bookingId", "occurrence", "step"] as const;

/**
 * The booking detail opens over whichever booking page you are on, through `?bookingId=` (plus
 * `occurrence` and `step`) in the URL. That keeps it linkable (emails and the agenda land on
 * it) and lets Back close it. Every other search param on the page (a tab, a filter) is kept.
 */
export function useBookingPanel() {
  const navigate = useNavigate();
  const location = useLocation();
  const search = location.search as Record<string, unknown>;
  const bookingId = typeof search.bookingId === "string" ? search.bookingId : undefined;
  const occurrenceStart = typeof search.occurrence === "string" ? search.occurrence : undefined;
  const step = BOOKING_PANEL_STEPS.find((candidate) => candidate === search.step);

  const withoutPanel = useCallback(() => {
    const rest: Record<string, unknown> = { ...search };
    for (const key of PANEL_KEYS) delete rest[key];
    return rest;
  }, [search]);

  const openBooking = useCallback(
    (next: string, options: OpenBookingOptions = {}) =>
      navigate({
        to: location.pathname,
        search: {
          ...withoutPanel(),
          bookingId: next,
          ...(options.occurrenceStart ? { occurrence: options.occurrenceStart } : {}),
          ...(options.step && options.step !== "overview" ? { step: options.step } : {}),
        },
      }),
    [navigate, location.pathname, withoutPanel],
  );

  const closeBooking = useCallback(
    () => navigate({ to: location.pathname, search: withoutPanel() }),
    [navigate, location.pathname, withoutPanel],
  );

  return { bookingId, occurrenceStart, step, openBooking, closeBooking };
}
